import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';

// Material UI
import LinkedIn from '@mui/icons-material/LinkedIn';
import Facebook from '@mui/icons-material/Facebook';

// Utils
import { useWindowWidth } from '../../hooks/useWindowSize';
import { usePolicies } from '../../hooks/usePolicies';
import { useLocationContext } from '../../providers/location.provider';
import { ROUTE_PATHS } from '../_pages/_utils/router.utils';

// Components
import { Row } from '../page-components-copy/components';

const Container = styled('div')`
  @media print {
    display: none;
  }
  background-color: #000;
  padding: 26px 0;
  display: flex;
  flex-direction: column;
  color: #fff;
  @media ${({ theme }) => theme.mq.desktopLargeMin} {
    flex-direction: row;
    padding: 93px 0;
  }
  & #copyright {
    font-size: 18px;
    line-height: 32px;
  }
`;

const Content = styled('nav')`
  display: flex;
  margin: auto;
  flex-direction: column;
  @media ${({ theme }) => theme.mq.desktopLargeMin} {
    flex-direction: row;
    max-width: 1200px;
  }
  & ul {
    list-style: none;
    margin: 0;
    & * {
      color: #fff;
    }
    & li {
      font-family: 'Majorant-Regular';
      font-style: normal;
      font-weight: 400;
      font-size: 18px;
      line-height: 32px;
      & a {
        text-decoration: none;
        padding: 4px 4px 4px 0;
      }
    }
    & p {
      font-size: 14px;
    }
  }
`;

const BigText = styled('span')`
  font-weight: 400;
  font-size: 24px;
  line-height: 37px;
  @media ${({ theme }) => theme.mq.desktopLargeMin} {
    font-size: 32px;
    line-height: 47px;
  }
`;

const LinksContainer = styled(Row)`
  align-items: flex-start;
  flex: 1;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 42px;
  flex-direction: column;
  @media ${({ theme }) => theme.mq.desktopLargeMin} {
    align-items: flex-start;
    flex-direction: row;
    justify-content: center;
    margin-bottom: 0;
  }
`;

const LinkRow = styled('ul')`
  display: flex;
  flex-direction: row;
  gap: 12px;
  @media ${({ theme }) => theme.mq.desktopLargeMin} {
    flex-direction: column;
  }
  & a {
    text-decoration: none;
  }
`;

const Footer = () => {
  const { isMobile } = useWindowWidth();
  const { isPuertoRico } = useLocationContext();
  const policies = usePolicies();
  // Privacy is always the in-app /privacy route: it hands PR users the governing
  // FCMC PR privacy PDF and sends everyone else on to the solve.finance policy.
  // PR has no separate Terms web page, so Terms still links straight to the PDF.
  const termsHref = isPuertoRico ? policies.FCMC_TERMS : 'https://www.solve.finance/tos/';
  return (
    <Container id="footer">
      <Content>
        <LinksContainer>
          <ul>
            <li>
              <a href="https://www.solve.finance/about-solve-finance">
                <Trans>About</Trans>
              </a>
            </li>

            <li>
              <a href="https://www.solve.finance/resources/">
                <Trans>Resources</Trans>
              </a>
            </li>

            <li>
              <Link to={ROUTE_PATHS.PRIVACY}>
                <Trans>Privacy Policy</Trans>
              </Link>
            </li>

            {!isPuertoRico && (
              <li>
                <a href="https://www.solve.finance/texas-short-term-loans/">
                  <Trans>Texas Short-Term Loans</Trans>
                </a>
              </li>
            )}

            <li>
              <a href={termsHref}>
                <Trans>Terms of Service</Trans>
              </a>
            </li>
          </ul>

          <LinkRow>
            <li>
              <a href="https://www.linkedin.com/company/solve-finance">
                {isMobile ? <LinkedIn fontSize="large" /> : 'LinkedIn'}
              </a>
            </li>
            <li>
              <a href="https://www.facebook.com/SolveFinance/">
                {isMobile ? <Facebook fontSize="large" /> : 'Facebook'}
              </a>
            </li>
          </LinkRow>
        </LinksContainer>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <ul data-testid="footerRight">
            <BigText>
              <Trans>Get in touch</Trans>
              {isMobile ? <br /> : ' '}
              <a href="mailto:info@solve.finance">info@solve.finance</a>
            </BigText>
          </ul>
          <ul>
            <li id="copyright">
              <Trans>© Copyright {new Date().getFullYear()}, Solve Finance</Trans>
            </li>
          </ul>
        </div>
      </Content>
    </Container>
  );
};

export default Footer;
